import { DEFAULT_INDEX_DECIMALS } from 'app-constants'
import { SymbolsAndValuesProps } from '../../../../notifications/index'
import { Transaction } from '../../../../../services'
import { TransactionReadout } from '../interface'
import { getAssetIconBySymbol } from '../../../../../abis/set/asset-icons'
import { shortValue } from '../../../../../utils/numbers-helper'
import { TransactionStatus } from 'sdk/omni/omni-transaction-service/types'

/**
 * Returns the symbols and values of the transaction, but shortens the values
 * @param transaction
 * @returns
 */
const getSymbolsAndValuesShort = (transaction: Transaction): SymbolsAndValuesProps => {
  const firstRawData = {
    value: transaction.action.indexDetails?.value || '0',
    decimals: transaction.action.indexDetails?.assetDecimals || DEFAULT_INDEX_DECIMALS,
  }

  const secondRawData = {
    value: transaction.action.stakingDetails?.value || '0',
    decimals: transaction.action.stakingDetails?.assetDecimals || DEFAULT_INDEX_DECIMALS,
  }

  const firstSymbol =
    transaction.action.indexDetails !== null ? transaction.action.indexDetails.indexTicker : ''
  const firstValue = firstRawData.value
  const secondSymbol =
    transaction.action.stakingDetails !== null ? transaction.action.stakingDetails.assetName : ''
  const secondValue = secondRawData.value

  return {
    firstSymbol,
    firstValue: shortValue(firstValue),
    secondSymbol,
    secondValue: shortValue(secondValue),
  }
}

export const extractTransactionDetails = (transaction: Transaction): TransactionReadout => {
  const { firstSymbol, firstValue, secondSymbol, secondValue } =
    getSymbolsAndValuesShort(transaction)
  let network = 1

  if (!transaction.action.network) {
    console.log('No network specified for transaction, using default')
  } else {
    network = transaction.action.network
  }

  const hasError = transaction.status === TransactionStatus.FAILED

  return {
    status: transaction.status,
    action: transaction.action.action,
    firstSymbol,
    firstValue,
    secondSymbol,
    secondValue,
    hash: transaction.replacementHash ? transaction.replacementHash : transaction.hash,
    network,
    hasError,
    additionalTransactions: transaction.additionalTransactions || [],
  }
}

export const getTenseSpecificWord = (word: string, isPastTense: boolean): string => {
  if (isPastTense) {
    switch (word) {
      case 'Approving':
        return 'Approved'
      case 'Swapping':
        return 'Swapped'
      case 'Staking':
        return 'Staked'
      case 'Unstaking':
        return 'Unstaked'
      case 'Buying':
      case 'Mint':
        return 'Bought'
      case 'Selling':
      case 'Redeem':
        return 'Sold'
      case 'Depositing':
        return 'Deposited'
      case 'Withdrawing':
        return 'Withdrawn'
      default:
        return word
    }
  }
  return word
}

export const textSwitch = (txDetails: TransactionReadout, pastTense = false): string => {
  const { action, firstSymbol, firstValue, secondSymbol, secondValue } = txDetails
  switch (action) {
    case 'approve':
      return `${getTenseSpecificWord('Approving', pastTense)} `
    case 'swap':
      return `${getTenseSpecificWord('Swapping', pastTense)} ${secondValue} ${secondSymbol} ${
        Number(firstValue) > 0 ? `for ${firstValue} ${firstSymbol}` : ` for ${firstSymbol}`
      } `
    case 'stake':
      return `${getTenseSpecificWord('Staking', pastTense)} ${secondValue} ${secondSymbol}`
    case 'unstake':
      return `${getTenseSpecificWord('Unstaking', pastTense)} ${secondValue} ${secondSymbol}`
    case 'buy':
    case 'mint':
      return `${getTenseSpecificWord('Buying', pastTense)} ${firstValue} ${firstSymbol}`
    case 'sell':
    case 'redeem':
    case 'omniRedeem':
      return `${getTenseSpecificWord('Selling', pastTense)} ${firstValue} ${firstSymbol}`
    case 'withdraw':
      return `${getTenseSpecificWord('Withdraw', pastTense)} from ${firstSymbol}`
    case 'retry':
      return `Retry transaction`
    default:
      return ''
  }
}

/**
 *
 * @param txDetails
 * @returns
 */

export const getIcon = (txDetails: TransactionReadout): string => {
  const { action, firstSymbol, secondSymbol } = txDetails

  switch (action) {
    case 'approve':
      return getAssetIconBySymbol(secondSymbol)
    case 'swap':
    case 'buy':
    case 'mint':
      return getAssetIconBySymbol(firstSymbol)

    case 'stake':
    case 'unstake':
      return getAssetIconBySymbol(secondSymbol)

    case 'omniRedeem':
    case 'retry':
    case 'sell':
    case 'redeem':
    case 'withdraw':
      return getAssetIconBySymbol(firstSymbol)

    default:
      throw new Error('Unexpected icon error')
  }
}
